
import React from 'react';
import Layout from '@/components/Layout/Layout';
import WeightConverter from '@/components/UnitConverter/WeightConverter';
import { Link } from 'react-router-dom';

const WeightConverterPage = () => {
  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4">
        {/* Header Section */}
        <div className="mb-8">
          <div className="text-sm text-gray-500 mb-2">
            <Link to="/" className="hover:underline">Home</Link> {' > '} 
            <Link to="/converters" className="hover:underline">Unit Converters</Link> {' > '} 
            <span>Weight Converter</span>
          </div>
          <h1 className="text-3xl font-bold mb-3">Weight and Mass Converter</h1>
          <p className="text-gray-700">
            Convert between different units of weight and mass including kilograms, pounds, ounces, grams, stones, and more. 
            Essential for cooking, shipping, fitness tracking, and scientific applications.
          </p>
        </div>

        {/* Converter Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <WeightConverter />
        </div>

        {/* Information Section */}
        <div className="bg-[#E6F3FF] p-6 rounded-lg mb-8">
          <h2 className="text-2xl font-semibold mb-3">How to use the Weight Converter</h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-2">
            <li>Enter the weight or mass value you want to convert</li>
            <li>Select the source unit from the comprehensive dropdown list</li>
            <li>Choose your target unit for conversion</li>
            <li>View the precise converted result instantly</li>
            <li>Switch between units easily using the swap function</li>
          </ol>
        </div>

        {/* Supported Units */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Supported Weight and Mass Units</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-xl font-medium mb-2">Metric System</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
                <li><strong>Milligram (mg):</strong> 1/1000 of a gram</li>
                <li><strong>Gram (g):</strong> Base unit for small masses</li>
                <li><strong>Kilogram (kg):</strong> 1000 grams, SI base unit</li>
                <li><strong>Metric Ton:</strong> 1000 kilograms</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-medium mb-2">Imperial System</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
                <li><strong>Ounce (oz):</strong> 1/16 of a pound</li>
                <li><strong>Pound (lb):</strong> 16 ounces</li>
                <li><strong>Stone (st):</strong> 14 pounds (UK)</li>
                <li><strong>US Ton:</strong> 2000 pounds</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-medium mb-2">Specialized Units</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
                <li><strong>Troy Ounce:</strong> Precious metals</li>
                <li><strong>Carat:</strong> Gemstones (200mg)</li>
                <li><strong>Grain:</strong> Pharmaceuticals</li>
                <li><strong>Hundredweight:</strong> Agriculture</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Common Conversions */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Common Weight Conversions</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium mb-2">Everyday Conversions:</h4>
                <ul className="space-y-1">
                  <li>1 kilogram = 2.20462 pounds</li>
                  <li>1 pound = 0.453592 kilograms</li>
                  <li>1 ounce = 28.3495 grams</li>
                  <li>1 stone = 6.35029 kilograms</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Cooking Measurements:</h4>
                <ul className="space-y-1">
                  <li>1 cup flour ≈ 120-125 grams</li>
                  <li>1 cup sugar ≈ 200 grams</li>
                  <li>1 cup butter ≈ 227 grams</li>
                  <li>1 tablespoon ≈ 15 grams</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">When to Use the Weight Converter</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
            <li><strong>Cooking & Baking:</strong> Convert recipe ingredients between metric and imperial measurements</li>
            <li><strong>Fitness & Health:</strong> Track body weight, convert supplement dosages, monitor nutrition</li>
            <li><strong>Shipping & Logistics:</strong> Calculate shipping costs, package weight limits, cargo planning</li>
            <li><strong>Science & Medicine:</strong> Laboratory measurements, pharmaceutical dosages, research data</li>
            <li><strong>International Trade:</strong> Convert product weights for global commerce and compliance</li>
            <li><strong>Precious Metals:</strong> Gold, silver trading using troy ounces and grams</li>
            <li><strong>Agriculture:</strong> Crop yields, livestock weights, feed calculations</li>
          </ul>
        </div>

        {/* Related Calculators */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Related Calculators and Converters</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/special/bmi" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">⚖️</span> BMI Calculator
            </Link>
            <Link to="/converters/volume" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">🥛</span> Volume Converter
            </Link>
            <Link to="/converters/length" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">📏</span> Length Converter
            </Link>
            <Link to="/special/calories" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">🍎</span> Calorie Calculator
            </Link>
            <Link to="/special/ideal-weight" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">⚖️</span> Ideal Weight Calculator
            </Link>
            <Link to="/converters/pressure" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">📊</span> Pressure Converter
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WeightConverterPage;
